<template>
  <div>
    <Menu />

    <header class="hero">
      <div class="container">
        <div class="hero-topbar d-flex align-items-center justify-content-between">

          <div class="brand-lockup">
            <a href="/home" class="d-flex align-items-center text-decoration-none">
              <img src="https://www.stratitude.co.za/chas-international/dubai/images/chas-logo.svg" alt="Chas Everitt" class="brand-logo" loading="lazy" />
            </a>

            <span class="brand-divider d-none d-md-inline-block" aria-hidden="true"></span>

            <div class="d-none d-md-block">
              <a href="https://www.leadingre.com/" class="d-flex align-items-center text-decoration-none" target="_blank" rel="noopener noreferrer">
                <img src="https://www.stratitude.co.za/chas-international/dubai/images/leading-logo1.png" alt="Leading Real Estate" class="partner-logo" loading="lazy" />
              </a>
            </div>
          </div>

          <nav class="d-none d-lg-flex hero-nav align-items-center">
            <a class="nav-link" href="/properties">Buy</a>
            <a class="nav-link" href="/sell">Sell</a>
            <a class="nav-link" href="/developments">Developments</a>
            <a class="nav-link" href="/commercial">Commercial</a>
          </nav>

          <button class="btn hamburger d-lg-none" type="button" data-bs-toggle="offcanvas" data-bs-target="#mobileNav">
            <i class="bi bi-list" style="font-size:1.25rem;"></i>
          </button>
        </div>

        <div class="pt-5 pb-4">
          <h1 class="hero-title mb-3">WELCOME<br />TO DUBAI</h1>
        </div>
      </div>
    </header>

    <section class="search-strip">
      <div class="container">
        <form class="row g-2 align-items-center" @submit.prevent="goToMatching">
          <div class="col-12 col-md-6 col-lg-2">
            <select class="form-select" v-model="selectedListingType">
              <option value="">BUY / RENT</option>
              <option v-for="type in listingTypes" :key="type" :value="type">
                {{ type }}
              </option>
            </select>
          </div>

          <div class="col-12 col-md-6 col-lg-2">
            <select class="form-select" v-model="selectedPropertyType">
              <option value="">PROPERTY TYPE</option>
              <option v-for="type in propertyTypes" :key="type" :value="type">
                {{ type }}
              </option>
            </select>
          </div>

          <div class="col-12 col-md-6 col-lg-3 position-relative">
            <input
              type="text"
              class="form-control"
              placeholder="ENTER A LOCATION"
              v-model="searchText"
              @focus="openLocationSuggestions"
              @input="openLocationSuggestions"
              @keydown.esc="closeLocationSuggestions"
            />

            <ul
              v-if="showLocationSuggestions && locationSuggestions.length > 0 && searchText != ''"
              class="list-group home-search-suggest"
            >
              <li
                v-for="suggestion in locationSuggestions"
                :key="suggestion"
                class="list-group-item"
                @mousedown.prevent="selectLocationSuggestion(suggestion)"
              >
                <div style="cursor:pointer;">
                  {{ suggestion }}
                </div>
              </li>
            </ul>
          </div>

          <div class="col-6 col-md-3 col-lg-1">
            <select class="form-select" v-model.number="selectedBeds">
              <option :value="0">BEDS</option>
              <option v-for="bed in bedOptions" :key="bed" :value="bed">
                {{ bed }}+
              </option>
            </select>
          </div>

          <div class="col-6 col-md-3 col-lg-1">
            <select class="form-select" v-model.number="selectedBaths">
              <option :value="0">BATHS</option>
              <option v-for="bath in bathOptions" :key="bath" :value="bath">
                {{ bath }}+
              </option>
            </select>
          </div>

          <div class="col-12 col-md-6 col-lg-2">
            <select class="form-select" v-model.number="selectedMaxPrice">
              <option :value="0">MAX PRICE</option>
              <option v-for="price in priceOptions" :key="price" :value="price">
                {{ formatCurrency(price) }}
              </option>
            </select>
          </div>

          <div class="col-12 col-lg-1">
            <button class="btn btn-primary" type="submit">
              Search
            </button>
          </div>
        </form>
      </div>
    </section>

    <section class="partners">
      <div class="container">
        <div class="row align-items-center g-3">
          <div class="col-12 col-md-3">
            <div class="section-title mb-1">Our Partners</div>
          </div>

          <div class="col-12 col-md-3 text-center text-md-start">
            <a href="https://www.leadingre.com/" target="_blank" rel="noopener noreferrer" class="d-inline-block text-decoration-none">
              <img src="https://www.stratitude.co.za/chas-international/dubai/images/leading-logo2.png" alt="Leading Real Estate Companies of the World" style="max-height:50px; width:auto;" loading="lazy" />
            </a>
          </div>

          <div class="col-12 col-md-6">
            <p>
              A commercial network comprised of more than 550 companies across 70 countries
              that is responsible for more than 12 million property transactions a year.
            </p>
          </div>
        </div>
      </div>
    </section>

    <section class="py-5 bg-white">
      <div class="container">
        <div class="section-title">Latest Listings</div>
        <div class="section-rule"></div>

        <div v-if="loading" class="py-4">Loading listings...</div>
        <div v-else-if="!latestListings.length" class="py-4">No listings found.</div>

        <div v-else class="row g-4">
          <div class="col-12 col-md-4" v-for="listing in latestListings" :key="listing.id">
            <article class="listing-card h-100">
              <a class="listing-card__media" :href="propertyHref(listing)">
                <div class="listing-img" :style="{ backgroundImage: 'url(' + featuredImage(listing) + ')' }"></div>
              </a>

              <div class="listing-meta text-start">
                <div class="listing-title">
                  <strong>{{ cleanText(listing.marketing_heading) }}</strong>
                </div>

                <div class="listing-price">
                  <div>{{ displayPrice(listing) }}</div>

                  <template v-if="hasConvertedPrice(listing)">
                    <div class="converted-price">
                      Approx. {{ displayConvertedPrice(listing, 'ZAR') }}
                    </div>
                    <div class="converted-price">
                      Approx. {{ displayConvertedPrice(listing, 'USD') }}
                    </div>
                  </template>
                </div>

                <p class="listing-desc">
                  {{ listing.loc_suburb }}, {{ listing.loc_region }}
                </p>

                <div class="listing-tags">
                  <span v-if="listing.listing_type">{{ listing.listing_type }}</span>
                  <span v-if="listing.property_type">{{ listing.property_type }}</span>
                </div>
              </div>

              <div class="listing-cta">
                <a class="link-view" :href="propertyHref(listing)">View</a>
              </div>
            </article>
          </div>
        </div>

        <div class="text-center mt-4">
          <a class="btn btn-all btn-danger" href="/properties">View All Properties</a>
        </div>
      </div>
    </section>

    <section class="why-buy">
      <div class="container">
        <div class="section-title text-center">Why Buy Real Estate in Dubai</div>
        <div class="section-rule"></div>

        <div class="row g-3 justify-content-center">
          <div class="col-6 col-md-3">
            <div class="why-item">
              <div class="why-icon"><i class="bi bi-percent"></i></div>
              <div class="why-label">Tax-Free Investment</div>
            </div>
          </div>

          <div class="col-6 col-md-3">
            <div class="why-item">
              <div class="why-icon"><i class="bi bi-heart-pulse"></i></div>
              <div class="why-label">Top Healthcare &amp; Schooling</div>
            </div>
          </div>

          <div class="col-6 col-md-3">
            <div class="why-item">
              <div class="why-icon"><i class="bi bi-building-check"></i></div>
              <div class="why-label">High-Rental Yields</div>
            </div>
          </div>

          <div class="col-6 col-md-3">
            <div class="why-item">
              <div class="why-icon"><i class="bi bi-globe2"></i></div>
              <div class="why-label">Global Location</div>
            </div>
          </div>
        </div>
      </div>
    </section>

    <section class="py-5 bg-white">
      <div class="container">
        <div class="row g-4">
          <div class="col-12 col-lg-6">
            <div class="cta-tile">
              <div class="cta-img" style="background-image:url('https://images.unsplash.com/photo-1564013799919-ab600027ffc6?auto=format&fit=crop&w=1600&q=80');"></div>
              <div class="cta-body">
                <div class="cta-title">Ready to Buy?</div>
                <p class="cta-text">
                  We’re positioned to help local and international buyers secure exceptional property opportunities.
                </p>
                <a class="cta-link" href="/properties">Find Out More</a>
              </div>
            </div>
          </div>

          <div class="col-12 col-lg-6">
            <div class="cta-tile">
              <div class="cta-img" style="background-image:url('https://images.unsplash.com/photo-1521791136064-7986c2920216?auto=format&fit=crop&w=1600&q=80');"></div>
              <div class="cta-body">
                <div class="cta-title">Ready to Sell?</div>
                <p class="cta-text">
                  Our team delivers strong local connections and trusted expertise in residential and luxury property services.
                </p>
                <a class="cta-link" href="/sell">List With Us</a>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>

    <section class="about-band">
      <div class="container">
        <div class="row align-items-center g-3">
          <div class="col-12 col-lg-4">
            <h3>About Chas Everitt Dubai</h3>
          </div>

          <div class="col-12 col-lg-8">
            <p class="text-white">
              With dual bases in Dubai and South Africa, we remain highly responsive and mobile,
              maintaining strong local connections across all property sectors.
            </p>
          </div>
        </div>
      </div>
    </section>

    <section class="team">
      <div class="container">
        <div class="section-title">Meet the Team</div>
        <div class="section-rule"></div>

        <div class="row g-4 align-items-start team-card">
          <div class="col-12 col-md-3">
            <div class="avatar">
              <img src="https://www.stratitude.co.za/chas-international/dubai/images/deon.png" alt="Deon du Plessis" style="width:100%; height:auto; object-fit:cover; display:block;" loading="lazy">
            </div>
          </div>

          <div class="col-12 col-md-9 person-block">
            <div class="d-flex flex-wrap align-items-baseline gap-2">
              <div class="person-name">Deon du Plessis</div>
              <div class="text-muted">|</div>
              <div class="person-role">Qualified Principal Property Practitioner</div>
              <div class="text-muted">|</div>
              <div class="text-muted">Chas Everitt International Dubai</div>
            </div>

            <h6>Role Focus:</h6>
            <p>Strategic direction, regulatory compliance, and long-term growth of the company.</p>

            <h6>Profile:</h6>
            <p>
              With a strong entrepreneurial background and extensive experience in building and scaling businesses,
              Deon brings disciplined, future-focused leadership to the brand.
            </p>
          </div>
        </div>

        <div class="row g-4 align-items-start team-card mt-4">
          <div class="col-12 col-md-3">
            <div class="avatar">
              <img src="https://www.stratitude.co.za/chas-international/dubai/images/janine.png" alt="Janine Lingenfelder" style="width:100%; height:auto; object-fit:cover; display:block;" loading="lazy">
            </div>
          </div>

          <div class="col-12 col-md-9 person-block">
            <div class="d-flex flex-wrap align-items-baseline gap-2">
              <div class="person-name">Janine Lingenfelder</div>
              <div class="text-muted">|</div>
              <div class="person-role">General Manager</div>
              <div class="text-muted">|</div>
              <div class="text-muted">Chas Everitt Dubai</div>
            </div>

            <h6>Role Focus:</h6>
            <p>Establishing the brand, supporting local &amp; international clients, and team development.</p>

            <h6>Profile:</h6>
            <p>
              With over two decades of experience in sales leadership and relationship-driven growth,
              Janine supports market expansion and acts as a key link between the global network and Dubai’s property market.
            </p>
          </div>
        </div>

        <div class="row g-4 align-items-start team-card mt-4">
          <div class="col-12 col-md-3">
            <div class="avatar">
              <img src="https://www.stratitude.co.za/chas-international/dubai/images/jessica.png" alt="Jessica Lingenfelder" style="width:100%; height:auto; object-fit:cover; display:block;" loading="lazy">
            </div>
          </div>

          <div class="col-12 col-md-9 person-block">
            <div class="d-flex flex-wrap align-items-baseline gap-2">
              <div class="person-name">Jessica Lingenfelder</div>
              <div class="text-muted">|</div>
              <div class="person-role">Property Consultant</div>
              <div class="text-muted">|</div>
              <div class="text-muted">Chas Everitt Dubai</div>
            </div>

            <h6>Role Focus:</h6>
            <p>
              Leading client relationships, supporting property transactions, and contributing to
              the growth of the Dubai real estate portfolio through strategic market engagement.
            </p>

            <h6>Profile:</h6>
            <p>
              Jessica brings extensive experience in property sales and international client relations.
              With a strong understanding of the Dubai property landscape, she focuses on delivering
              tailored solutions for investors and homeowners while building long-term relationships across local and global markets.
            </p>
          </div>
        </div>
      </div>
    </section>

    <section class="join-band">
      <div class="container">
        <div class="row align-items-center g-3">
          <div class="col-12 col-lg-9">
            <h4>Don't join a real estate company. Become part of a family.</h4>
          </div>
          <div class="col-12 col-lg-3 text-lg-end">
            <a class="btn btn-danger" href="https://www.chaseveritt.co.za/join-us/" target="_blank">Join Our Team</a>
          </div>
        </div>
      </div>
    </section>

    <div class="offcanvas offcanvas-end offcanvas-custom" tabindex="-1" id="mobileNav" aria-labelledby="mobileNavLabel">
      <div class="offcanvas-header">
        <h5 class="offcanvas-title text-white" id="mobileNavLabel">Menu</h5>
        <button type="button" class="btn-close btn-close-white" data-bs-dismiss="offcanvas" aria-label="Close"></button>
      </div>

      <div class="offcanvas-body">
        <div class="list-group list-group-flush">
          <a class="list-group-item list-group-item-action" href="/properties">Buy</a>
          <a class="list-group-item list-group-item-action" href="/sell">Sell</a>
          <a class="list-group-item list-group-item-action" href="/developments">Developments</a>
          <a class="list-group-item list-group-item-action" href="/commercial">Commercial</a>
        </div>
        <hr />
      </div>
    </div>

    <Footer />
  </div>
</template>

<script>
import axios from 'axios'
import Menu from '@/components/Menu.vue'
import Footer from '@/components/Footer.vue'

export default {
  components: {
    Menu,
    Footer
  },

  data() {
    return {
      residentialListings: [],
      commercialListings: [],
      loading: true,
      currencyRates: null,
      selectedListingType: '',
      selectedPropertyType: '',
      searchText: '',
      selectedBeds: 0,
      selectedBaths: 0,
      selectedMaxPrice: 0,
      showLocationSuggestions: false
    }
  },

  mounted() {
    this.fetchCurrencyRates()
    this.fetchListings()
  },

  computed: {
    allListings() {
      const residential = this.normaliseListings(this.residentialListings, 'residential')
      const commercial = this.normaliseListings(this.commercialListings, 'commercial')

      return residential.concat(commercial)
    },

    activeListings() {
      return this.allListings.filter((listing) => {
        const status = this.safeString(listing.status).toLowerCase()

        if (status == 'archived') {
          return false
        }

        if (!Array.isArray(listing.listing_images)) {
          return false
        }

        if (listing.listing_images.length == 0) {
          return false
        }

        return true
      })
    },

    latestListings() {
      const listings = this.activeListings.slice()

      listings.sort((a, b) => {
        const dateA = new Date(a.date_added || a.last_modified || 0).getTime()
        const dateB = new Date(b.date_added || b.last_modified || 0).getTime()

        if (dateB != dateA) {
          return dateB - dateA
        }

        return this.priceNumber(b.price) - this.priceNumber(a.price)
      })

      return listings.slice(0, 3)
    },

    listingTypes() {
      return this.uniqueSortedValues('listing_type')
    },

    propertyTypes() {
      return this.uniqueSortedValues('property_type')
    },

    bedOptions() {
      return this.uniqueSortedNumbers('bedrooms')
    },

    bathOptions() {
      return this.uniqueSortedNumbers('bathrooms')
    },

    priceOptions() {
      const prices = this.activeListings
        .map((listing) => {
          return this.priceNumber(listing.price)
        })
        .filter((price) => {
          return price > 0 && price != 999999999
        })

      const roundedPrices = prices.map((price) => {
        if (price <= 250000) {
          return Math.ceil(price / 50000) * 50000
        }

        if (price <= 1000000) {
          return Math.ceil(price / 100000) * 100000
        }

        return Math.ceil(price / 1000000) * 1000000
      })

      return Array.from(new Set(roundedPrices)).sort((a, b) => {
        return a - b
      })
    },

    locationSuggestions() {
      const text = this.searchText.toLowerCase()

      const locations = this.activeListings.map((listing) => {
        const suburb = listing.loc_suburb || ''
        const region = listing.loc_region || ''

        if (suburb != '' && region != '') {
          return suburb + ', ' + region
        }

        if (suburb != '') {
          return suburb
        }

        return region
      })

      return Array.from(new Set(locations))
        .filter((location) => {
          if (location == '') {
            return false
          }

          return location.toLowerCase().includes(text)
        })
        .sort()
        .slice(0, 8)
    }
  },

  methods: {
    async fetchCurrencyRates() {
      try {
        const response = await axios.get(
          'https://chas-dubai.stratitude-develop.co.za/api/fetch-currency-rates.php'
        )

        if (!response.data || response.data.success !== true) {
          return
        }

        this.currencyRates = {
          aedToZar: response.data.aedToZar,
          aedToUsd: response.data.aedToUsd,
          updatedAt: response.data.updatedAt,
          cachedAt: response.data.cachedAt
        }
      } catch (error) {
        console.log(error)
        this.currencyRates = null
      }
    },

    async fetchListings() {
      this.loading = true

      try {
        const residentialRequest = axios.get(
          'https://chas-dubai.stratitude-develop.co.za/api/fetch-listings-residential.php',
          {
            headers: {
              Accept: 'application/json'
            }
          }
        )

        const commercialRequest = axios.get(
          'https://chas-dubai.stratitude-develop.co.za/api/fetch-listings-commercial.php',
          {
            headers: {
              Accept: 'application/json'
            }
          }
        )

        const responses = await Promise.all([
          residentialRequest,
          commercialRequest
        ])

        this.residentialListings = responses[0].data
        this.commercialListings = responses[1].data
      } catch (error) {
        console.log(error)
        this.residentialListings = []
        this.commercialListings = []
      }

      this.loading = false
    },

    normaliseListings(listings, sourceType) {
      if (!Array.isArray(listings)) {
        return []
      }

      return listings
        .filter((listing) => {
          if (!listing) {
            return false
          }

          if (!listing[0]) {
            return false
          }

          return true
        })
        .map((listing) => {
          const item = Object.assign({}, listing[0])
          item.source_type = sourceType

          return item
        })
    },

    uniqueSortedValues(fieldName) {
      const values = this.activeListings
        .map((listing) => {
          return listing[fieldName]
        })
        .filter((value) => {
          if (!value) {
            return false
          }

          return true
        })

      return Array.from(new Set(values)).sort()
    },

    uniqueSortedNumbers(fieldName) {
      const values = this.activeListings
        .map((listing) => {
          return this.numberValue(listing[fieldName])
        })
        .filter((value) => {
          return value > 0
        })

      return Array.from(new Set(values)).sort((a, b) => {
        return a - b
      })
    },

    openLocationSuggestions() {
      this.showLocationSuggestions = true
    },

    closeLocationSuggestions() {
      this.showLocationSuggestions = false
    },

    selectLocationSuggestion(suggestion) {
      this.searchText = suggestion
      this.closeLocationSuggestions()
    },

    goToMatching() {
      this.closeLocationSuggestions()

      if (this.$store && this.$store.state) {
        this.$store.state.selectedListingType = this.selectedListingType
        this.$store.state.selectedPropertyType = this.selectedPropertyType
        this.$store.state.searchText = this.searchText
        this.$store.state.selectedBeds = this.selectedBeds
        this.$store.state.selectedBaths = this.selectedBaths
        this.$store.state.selectedMaxPrice = this.selectedMaxPrice

        if (this.$store.state.search) {
          this.$store.state.search.text = this.searchText
        }

        this.$store.state.filterType = this.selectedListingType
        this.$store.state.filterProperty = this.selectedPropertyType
      }

      const query = {}

      if (this.selectedListingType != '') {
        query.listing_type = this.selectedListingType
      }

      if (this.selectedPropertyType != '') {
        query.property_type = this.selectedPropertyType
      }

      if (this.searchText != '') {
        query.location = this.searchText
      }

      if (this.selectedBeds > 0) {
        query.beds = this.selectedBeds
      }

      if (this.selectedBaths > 0) {
        query.baths = this.selectedBaths
      }

      if (this.selectedMaxPrice > 0) {
        query.max_price = this.selectedMaxPrice
      }

      if (this.$router) {
        this.$router.push({
          path: '/properties',
          query: query
        })
      } else {
        const params = new URLSearchParams(query).toString()

        if (params != '') {
          window.location.href = '/properties?' + params
        } else {
          window.location.href = '/properties'
        }
      }
    },

    propertyHref(listing) {
      if (!listing || !listing.id) {
        return '/properties'
      }

      return '/property/' + listing.id
    },

    featuredImage(listing) {
      if (!listing || !Array.isArray(listing.listing_images)) {
        return ''
      }

      if (listing.listing_images.length == 0) {
        return ''
      }

      return listing.listing_images[0]
    },

    displayPrice(listing) {
      if (!listing) {
        return ''
      }

      if (this.isTruthy(listing.poa)) {
        return 'POA'
      }

      if (parseInt(listing.price, 10) == 999999999) {
        return 'POA'
      }

      return this.formatCurrency(listing.price)
    },

    displayConvertedPrice(listing, currency) {
      if (!listing) {
        return ''
      }

      if (!this.currencyRates) {
        return ''
      }

      if (this.isTruthy(listing.poa)) {
        return ''
      }

      const price = parseInt(listing.price, 10)

      if (isNaN(price)) {
        return ''
      }

      if (price == 999999999) {
        return ''
      }

      if (currency == 'ZAR') {
        return new Intl.NumberFormat('en-ZA', {
          style: 'currency',
          currency: 'ZAR',
          maximumFractionDigits: 0
        }).format(price * this.currencyRates.aedToZar)
      }

      if (currency == 'USD') {
        return new Intl.NumberFormat('en-US', {
          style: 'currency',
          currency: 'USD',
          maximumFractionDigits: 0
        }).format(price * this.currencyRates.aedToUsd)
      }

      return ''
    },

    hasConvertedPrice(listing) {
      if (!listing) {
        return false
      }

      if (!this.currencyRates) {
        return false
      }

      if (this.isTruthy(listing.poa)) {
        return false
      }

      const price = parseInt(listing.price, 10)

      if (isNaN(price)) {
        return false
      }

      if (price == 999999999) {
        return false
      }

      return true
    },

    formatCurrency(value) {
      const amount = parseInt(value, 10)

      if (isNaN(amount)) {
        return 'AED 0'
      }

      return new Intl.NumberFormat('en-AE', {
        style: 'currency',
        currency: 'AED',
        maximumFractionDigits: 0
      }).format(amount)
    },

    cleanText(value) {
      if (!value) {
        return ''
      }

      return value
        .toString()
        .replace(/ΓÇö/g, '—')
        .replace(/ΓÇô/g, '–')
        .replace(/ΓÇÖ/g, '’')
        .replace(/ΓÇ£/g, '“')
        .replace(/ΓÇ¥/g, '”')
        .replace(/ΓÇó/g, '•')
        .replace(/Γ£ö∩╕Å/g, '✓')
        .replace(/≡ƒôì/g, '📍')
        .replace(/ΓÇª/g, '…')
        .replace(/┬á/g, ' ')
        .replace(/&amp;/g, '&')
    },

    priceNumber(value) {
      const price = parseInt(value, 10)

      if (isNaN(price)) {
        return 0
      }

      return price
    },

    numberValue(value) {
      const number = parseFloat(value)

      if (isNaN(number)) {
        return 0
      }

      return number
    },

    safeString(value) {
      if (!value) {
        return ''
      }

      return value.toString()
    },

    isTruthy(value) {
      if (value === true) {
        return true
      }

      if (value == 'true') {
        return true
      }

      if (value == 1) {
        return true
      }

      return false
    }
  }
}
</script>

<style scoped>
.home-search-suggest {
  position: absolute;
  z-index: 20;
  width: calc(100% - 24px);
  left: 12px;
  right: 12px;
}

.listing-img {
  width: 100%;
  height: 280px;
  background-size: cover;
  background-position: center;
}

.listing-tags {
  display: flex;
  flex-wrap: wrap;
  gap: 6px;
  margin-top: 12px;
}

.listing-tags span {
  background-color: #255699;
  color: #fff;
  font-size: 11px;
  font-weight: bold;
  padding: 5px 12px;
}
.converted-price {
  font-size: 0.85em;
  font-weight: 400;
  opacity: 0.75;
  line-height: 1.3;
}

</style>